/**
 * Small shared panels: comparison tray, value-score breakdown, insights,
 * notices, and the personal-targets contribution readout.
 */

import { Link } from 'react-router-dom';
import type { ValueScore } from '@/lib/nutrition/scoring';
import type { Insight } from '@/lib/nutrition/ranking';
import type { ProductMetrics } from '@/lib/nutrition/calculations';
import { formatMetricValue, formatPercent, providerLabel } from '@/lib/format';
import type { Targets } from '@/state/AppState';
import { trimNumber } from '@/lib/nutrition/units';

// ---------------------------------------------------------------------------
// Comparison tray (sticky, mobile-first — section 27)
// ---------------------------------------------------------------------------

export function CompareTray({
  items,
  onRemove,
  onClear,
}: {
  items: Array<{ id: string; name: string }>;
  onRemove: (id: string) => void;
  onClear: () => void;
}): React.JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <div className="tray" role="region" aria-label="Comparison tray">
      <div className="tray-inner">
        <div className="tray-items">
          {items.map((item) => (
            <span className="tray-chip" key={item.id}>
              <span>{item.name}</span>
              <button
                type="button"
                className="tray-remove"
                onClick={() => onRemove(item.id)}
                aria-label={`Remove ${item.name} from comparison`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <button type="button" className="btn btn-sm" onClick={onClear}>
          Clear
        </button>
        <Link
          className={`btn btn-sm${items.length >= 2 ? ' btn-primary' : ''}`}
          to="/compare"
          aria-disabled={items.length < 2}
          onClick={(e) => {
            if (items.length < 2) e.preventDefault();
          }}
        >
          Compare ({items.length})
        </Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Value score breakdown (section 14 — the calculation is never hidden)
// ---------------------------------------------------------------------------

export function ValueScorePanel({
  score,
  metrics,
  topMetricLabel,
}: {
  score: ValueScore;
  metrics: ProductMetrics;
  topMetricLabel: string | null;
}): React.JSX.Element {
  return (
    <section className="panel" aria-labelledby="score-heading">
      <h2 className="panel-title" id="score-heading">
        Value score
      </h2>

      {score.score === null ? (
        <p className="na" style={{ fontSize: '0.85rem', margin: 0 }}>
          {score.unavailableReason ?? 'Not enough data to score this product.'}
        </p>
      ) : (
        <>
          <div className="score-head">
            <span className="score-value">{score.score}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
              / 100 relative to these results
            </span>
          </div>

          <div className="score-components">
            {score.components.map((c) => (
              <div className="score-row" key={c.metric}>
                <span>{c.label}</span>
                <span className="score-bar">
                  <span
                    className="score-bar-fill"
                    style={{ width: `${Math.round((c.normalized ?? 0) * 100)}%` }}
                  />
                </span>
                <span className="score-raw" title={c.unavailableReason ?? undefined}>
                  {c.normalized === null
                    ? 'N/A'
                    : formatMetricValue(c.metric, { value: c.raw, unavailableReason: null }, metrics.currency, metrics.packageKind)}
                </span>
              </div>
            ))}
          </div>

          <p style={{ fontSize: '0.74rem', color: 'var(--text-muted)', margin: '0.6rem 0 0' }}>
            Each component is scaled against the best and worst value in the current results, then
            weighted. Weights:{' '}
            {score.components.map((c) => `${c.label} ${Math.round(c.weight * 100)}%`).join(', ')}.
            This measures value for money, not whether a food is healthy.
          </p>

          {topMetricLabel && (
            <p style={{ fontSize: '0.8rem', margin: '0.4rem 0 0' }}>
              Ranks <strong>#1</strong> for {topMetricLabel}.
            </p>
          )}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Insights (section 33)
// ---------------------------------------------------------------------------

export function InsightList({ insights }: { insights: Insight[] }): React.JSX.Element | null {
  if (insights.length === 0) return null;
  return (
    <div className="insights">
      {insights.map((insight) => (
        <p key={insight.text} className={`insight insight-${insight.kind}`}>
          <span aria-hidden="true">{insight.kind === 'warning' ? '⚠' : '◆'}</span>
          <span>{insight.text}</span>
        </p>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notices
// ---------------------------------------------------------------------------

export function Notice({
  kind,
  title,
  children,
}: {
  kind: 'info' | 'warn' | 'error';
  title?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className={`notice notice-${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      <div>
        {title && <strong>{title} </strong>}
        {children}
      </div>
    </div>
  );
}

/**
 * Provenance line (sections 22 & 34). States the source, when it was fetched,
 * and — crucially — whether what you are looking at came from a cache.
 */
export function ProvenanceLine({
  source,
  generatedAt,
  cached,
  fromCache,
  isSampleData,
}: {
  source: string;
  generatedAt: string;
  cached: boolean;
  fromCache: boolean;
  isSampleData: boolean;
}): React.JSX.Element {
  return (
    <p className="provenance">
      <span>
        Source: <strong>{providerLabel(source)}</strong>
      </span>
      <span>Retrieved {new Date(generatedAt).toLocaleString()}</span>
      {(cached || fromCache) && (
        <span className="badge badge-warn">
          {fromCache ? 'Offline cache — may be out of date' : 'Served from cache'}
        </span>
      )}
      {isSampleData && <span className="badge badge-warn">Sample data, not live prices</span>}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Personal targets contribution (section 21)
// ---------------------------------------------------------------------------

export function TargetContribution({
  metrics,
  targets,
}: {
  metrics: ProductMetrics;
  targets: Targets;
}): React.JSX.Element | null {
  const pkg = metrics.perPackage?.nutrients;
  const rows: Array<{ label: string; value: number; target: number; unit: string }> = [];

  if (targets.dailyProtein && pkg?.protein != null) {
    rows.push({ label: 'Daily protein', value: pkg.protein, target: targets.dailyProtein, unit: 'g' });
  }
  if (targets.dailyKcal && pkg?.kcal != null) {
    rows.push({ label: 'Daily calories', value: pkg.kcal, target: targets.dailyKcal, unit: 'kcal' });
  }

  if (rows.length === 0) return null;

  return (
    <section className="panel">
      <h2 className="panel-title">Contribution to your targets</h2>
      {rows.map((row) => {
        const pct = (row.value / row.target) * 100;
        return (
          <div key={row.label} style={{ marginBottom: '0.6rem' }}>
            <div className="kv-row">
              <dt style={{ color: 'var(--text-secondary)' }}>{row.label}</dt>
              <dd style={{ margin: 0, fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                {trimNumber(row.value, 0)} {row.unit} of {trimNumber(row.target, 0)} {row.unit} ·{' '}
                {formatPercent(pct)}
              </dd>
            </div>
            <div className="target-bar">
              <div
                className="target-bar-fill"
                style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
              />
            </div>
          </div>
        );
      })}
      <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: 0 }}>
        Based on the whole package. These are the targets you entered — this app does not recommend
        targets or give dietary advice.
      </p>
    </section>
  );
}

